import { useMemo, useRef, useCallback, useEffect } from "react";
import type { AgGridReact } from "ag-grid-react";
import type { ColDef, SideBarDef } from "ag-grid-community";
import type { IAGBlotterDataRow, UseBlotterAGTableProps, BlotterAlgoTableProps } from "../AlgoTable.types";
import type { IAGAmtsDataRow, IAGPosQtysDataRow } from "../AlgoTable.types";
import type { BlotterFeedType } from "@/pages/Blotter/services/blotterApi";
import {
    DETAIL_FEEDS,
    POSITION_UPDATE_DETAIL_COL_DEFS,
    BALANCE_UPDATE_DETAIL_COL_DEFS,
    getColDefsForFeed,
} from "../consts";
import { POSITION_UPDATE } from "@/pages/Blotter/services/consts";
import BlotterControlsPanel from "../components/BlotterControlsPanel";

export function useAGTable({ feedType, rowData, sidebarPanelProps }: UseBlotterAGTableProps & { sidebarPanelProps?: BlotterAlgoTableProps["sidebarPanelProps"] }) {
    const gridRef = useRef<AgGridReact<IAGBlotterDataRow> | null>(null);
    const prevFeedTypeRef = useRef<BlotterFeedType>(feedType);

    const colDefs = useMemo<ColDef<IAGBlotterDataRow>[]>(
        () => getColDefsForFeed(feedType),
        [feedType]
    );

    const defaultColDef = useMemo<ColDef>(
        () => ({
            filter: true,
            sortable: true,
            enableRowGroup: true,
        }),
        []
    );

    const masterDetail = DETAIL_FEEDS.has(feedType);

    // Custom sidebar configuration
    const sideBar = useMemo<SideBarDef>(() => {
        const toolPanels: any[] = [
            {
                id: "blotterControls",
                labelDefault: "Blotter Controls",
                labelKey: "blotterControls",
                iconKey: "menu",
                toolPanel: BlotterControlsPanel,
                toolPanelParams: sidebarPanelProps,
            },
            {
                id: "columns",
                labelDefault: "Columns",
                labelKey: "columns",
                iconKey: "columns",
                toolPanel: "agColumnsToolPanel",
            },
            {
                id: "filters",
                labelDefault: "Filters",
                labelKey: "filters",
                iconKey: "filter",
                toolPanel: "agFiltersToolPanel",
            },
        ];

        return {
            toolPanels,
            defaultToolPanel: "blotterControls",
        };
    }, [sidebarPanelProps]);

    const detailCellRendererParams = useMemo(() => {
        if (!DETAIL_FEEDS.has(feedType)) return undefined;
        const isPosition = feedType === POSITION_UPDATE;
        return {
            detailGridOptions: {
                columnDefs: isPosition
                    ? POSITION_UPDATE_DETAIL_COL_DEFS
                    : BALANCE_UPDATE_DETAIL_COL_DEFS,
                defaultColDef: {
                    sortable: true,
                    filter: true,
                    resizable: true,
                },
                domLayout: "normal",
                detailRowHeight: 200,
            },
            getDetailRowData: (params: {
                data: IAGBlotterDataRow;
                successCallback: (rows: IAGPosQtysDataRow[] | IAGAmtsDataRow[]) => void;
            }) => {
                const data = isPosition
                    ? (params.data as { pos_qtys?: IAGPosQtysDataRow[] }).pos_qtys
                    : (params.data as { amts?: IAGAmtsDataRow[] }).amts;
                params.successCallback(Array.isArray(data) ? data : []);
            },
        };
    }, [feedType]);

    // Auto-size all columns to fit content when data first renders
    const onFirstDataRendered = useCallback(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.autoSizeAllColumns();
        }
    }, []);

    // Auto-size columns only when feed type changes (not on pagination)
    useEffect(() => {
        if (feedType !== prevFeedTypeRef.current && gridRef.current?.api && rowData.length > 0) {
            // Small delay to ensure columns are rendered
            setTimeout(() => {
                if (gridRef.current?.api) {
                    gridRef.current.api.autoSizeAllColumns();
                }
            }, 100);
            prevFeedTypeRef.current = feedType;
        }
    }, [feedType, rowData.length]);

    return {
        gridRef,
        rowData,
        colDefs,
        defaultColDef,
        masterDetail,
        detailCellRendererParams,
        sideBar,
        onFirstDataRendered,
    };
}